import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Badge, cn } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  FUNNEL_WRITE_ROLES,
  getFunnelLiveUrl,
  getStatusBadgeVariant,
} from '../../lib/funnelUtils';
import * as FiIcons from 'react-icons/fi';

const {
  FiPlus, FiEdit2, FiSettings, FiExternalLink, FiCopy, FiTrash2, FiFilter,
} = FiIcons;

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? format(d, 'MMM d, yyyy') : '—';
}

export default function FunnelList() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const canWrite = FUNNEL_WRITE_ROLES.includes(user?.role);

  const loadFunnels = useCallback(async () => {
    if (!user?.agency_id) return;
    setLoading(true);
    try {
      const data = await api.funnels.getAll(user.agency_id);
      setFunnels(data ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load funnels', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, addToast]);

  useEffect(() => {
    if (user?.agency_id) loadFunnels();
  }, [user?.agency_id, loadFunnels]);

  const handleDuplicate = async (funnel) => {
    setActionLoading(true);
    try {
      const copy = await api.funnels.duplicateFunnel(funnel.id);
      addToast({ type: 'success', title: 'Funnel duplicated' });
      navigate(`/funnels/${copy.id}`);
    } catch (err) {
      addToast({ type: 'error', title: 'Duplicate failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.funnels.delete(deleteTarget.id);
      addToast({ type: 'success', title: 'Funnel deleted' });
      setDeleteTarget(null);
      await loadFunnels();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funnels</h1>
          <p className="text-sm text-gray-500">Build and manage recruiting &amp; sales funnels for your agency.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/funnels/templates')}>
            <SafeIcon icon={FiFilter} className="mr-2" />
            Templates
          </Button>
          {canWrite && (
            <Button onClick={() => navigate('/funnels/new')}>
              <SafeIcon icon={FiPlus} className="mr-2" />
              New Funnel
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-sm text-gray-500">Loading funnels…</p>
        </div>
      ) : funnels.length === 0 ? (
        <EmptyState
          icon={FiFilter}
          title="No funnels yet"
          description={
            canWrite
              ? 'Create your first funnel to capture leads and guide prospects through your offer.'
              : 'Your agency has not created any funnels yet.'
          }
          actionLabel={canWrite ? 'Create Funnel' : undefined}
          onAction={canWrite ? () => navigate('/funnels/new') : undefined}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Slug</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Steps</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Submissions</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Created</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {funnels.map((funnel) => (
                  <tr key={funnel.id} className="hover:bg-gray-50/80">
                    <td className="px-6 py-4">
                      <Link
                        to={`/funnels/${funnel.id}`}
                        className="font-medium text-primary hover:text-primary-dark hover:underline"
                      >
                        {funnel.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-xs">{funnel.slug}</td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusBadgeVariant(funnel.status)}>
                        {funnel.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{funnel.step_count ?? 0}</td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/funnels/${funnel.id}/submissions`}
                        className="text-primary hover:underline"
                      >
                        {funnel.submission_count ?? 0}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(funnel.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => navigate(`/funnels/${funnel.id}`)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="Settings"
                          onClick={() => navigate(`/funnels/${funnel.id}/settings`)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
                        >
                          <SafeIcon icon={FiSettings} className="w-4 h-4" />
                        </button>
                        {funnel.status === 'active' && (
                          <a
                            href={getFunnelLiveUrl(funnel.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View Live"
                            className="p-1.5 rounded-md text-gray-400 hover:text-green-600 hover:bg-green-50"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                          </a>
                        )}
                        {canWrite && (
                          <>
                            <button
                              type="button"
                              title="Duplicate"
                              disabled={actionLoading}
                              onClick={() => handleDuplicate(funnel)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
                            >
                              <SafeIcon icon={FiCopy} className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              disabled={actionLoading}
                              onClick={() => setDeleteTarget(funnel)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete funnel?">
        <p className="text-sm text-gray-600 mt-2">
          Permanently delete <span className="font-medium text-gray-900">{deleteTarget?.name}</span>?
          All steps, blocks, and submissions will be removed. This cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirm} isLoading={actionLoading}>
            Yes, Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
